<script lang="ts">
  import NavigationLoading from './NavigationLoading.svelte'
  import { page, Link } from '@inertiajs/svelte'
  import ChevronDown from '@lucide/svelte/icons/chevron-down'
  import Compass from '@lucide/svelte/icons/compass'
  import Library from '@lucide/svelte/icons/library'
  import Settings from '@lucide/svelte/icons/settings'
  import Users from '@lucide/svelte/icons/users'
  import Mail from '@lucide/svelte/icons/mail'
  import Send from '@lucide/svelte/icons/send'
  import BookOpen from '@lucide/svelte/icons/book-open'
  import Camera from '@lucide/svelte/icons/camera'
  import Award from '@lucide/svelte/icons/award'
  import Trophy from '@lucide/svelte/icons/trophy'
  import TrendingUp from '@lucide/svelte/icons/trending-up'
  import Star from '@lucide/svelte/icons/star'
  import Shuffle from '@lucide/svelte/icons/shuffle'
  import FlashMessage from './FlashMessage.svelte'
  import Button from './ui/Button.svelte'
  import Dropdown from './Dropdown.svelte'
  import AlgoliaSearch from './AlgoliaSearch.svelte'
  import { inboxCount, loadInboxCount } from '@/stores/cable'
  import {
    root_path,
    discover_path,
    books_path,
    clubs_path,
    challenges_path,
    featured_path,
    awards_path,
    bestsellers_path,
    profile_path,
    inbox_path,
    recommendations_path,
    settings_path,
    sign_in_path,
    sign_up_path,
    ocr_scans_path,
    random_path,
    catalog_books_path,
    admin_root_path,
  } from '@/routes'
  import { onMount } from 'svelte'
  import { preloadOnIntent, type PageIntentEvent } from '@/utils/pageIntent'

  let currentUser = $derived(page.props.current_user)
  let currentPath = $derived(page.url)
  let catalogPath = $derived(
    currentUser?.is_superuser ? admin_root_path() : catalog_books_path()
  )
  let algolia = $derived(
    page.props.algolia as
      | { application_id: string; search_api_key: string; index_name: string }
      | undefined
  )

  onMount(() => {
    if (currentUser) {
      loadInboxCount()
    }
  })

  function isActive(path: string): boolean {
    return currentPath.startsWith(path)
  }

  function getLinkClass(path: string): string {
    const baseClasses =
      'flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-2 text-sm font-medium transition shrink-0 border-b-2'
    if (isActive(path)) {
      return `${baseClasses} text-neutral-900 dark:text-white border-neutral-900 dark:border-white`
    }
    return `${baseClasses} text-neutral-600 dark:text-gray-300 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-200 dark:hover:bg-gray-700 border-transparent`
  }
</script>

<nav
  class="hidden md:block bg-white dark:bg-gray-800 border-b border-neutral-200 dark:border-gray-700"
>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="flex justify-between h-16">
      <div class="flex items-center gap-2 min-w-0">
        <Link
          href={root_path()}
          prefetch={true}
          cacheFor={['5s', '30s']}
          class="text-2xl lg:text-3xl font-bookshop tracking-tight text-neutral-800 dark:text-gray-100 whitespace-nowrap"
          >Reading Now</Link
        >
        <NavigationLoading />
        {#if currentUser?.can_manage_catalog}
          <Link
            href={catalogPath}
            class="text-xs sm:text-sm text-neutral-500 dark:text-gray-400 hover:text-neutral-700 dark:hover:text-gray-300 whitespace-nowrap"
            >({currentUser.is_superuser ? 'Admin' : 'Catalog'})</Link
          >
        {/if}
      </div>

      <div class="flex items-center space-x-1 lg:space-x-4 min-w-0">
        {#if currentUser}
          <!-- Algolia Search -->
          {#if algolia}
            <AlgoliaSearch
              applicationId={algolia.application_id}
              indexName={algolia.index_name}
            />
          {/if}

          <Link href={books_path()} class={getLinkClass(books_path())}>
            <BookOpen size={16} />
            <span class="hidden sm:inline">Books</span>
          </Link>
          <Link href={clubs_path()} class={getLinkClass(clubs_path())}>
            <Users size={16} />
            <span class="hidden sm:inline">Clubs</span>
          </Link>
          <Link href={featured_path()} class={getLinkClass(featured_path())}>
            <Star size={16} />
            <span class="hidden sm:inline">Featured</span>
          </Link>
          <Link href={awards_path()} class={getLinkClass(awards_path())}>
            <Trophy size={16} />
            <span class="hidden sm:inline">Awards</span>
          </Link>
          <Link
            href={profile_path(currentUser.username)}
            onpointerdown={(event: PageIntentEvent) =>
              preloadOnIntent('profiles/show', event)}
            onfocus={() => preloadOnIntent('profiles/show')}
            class={getLinkClass(profile_path(currentUser.username))}
          >
            <Library size={16} class="shrink-0" />
            <span class="hidden sm:inline">Reading</span>
          </Link>
          <Dropdown width="w-48">
            {#snippet trigger()}
              <div
                class="flex items-center gap-1 px-2 lg:px-4 py-2 text-sm font-medium text-neutral-600 dark:text-gray-300 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-200 dark:hover:bg-gray-700 rounded-md transition relative shrink-0"
              >
                <span class="hidden sm:inline">Menu</span>
                <ChevronDown size={16} />
                {#if $inboxCount > 0}
                  <span
                    class="absolute -top-1 -right-1 bg-danger-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                  >
                    {$inboxCount > 9 ? '9+' : $inboxCount}
                  </span>
                {/if}
              </div>
            {/snippet}
            {#snippet content()}
              <div class="py-1">
                <Link
                  href={inbox_path()}
                  class="flex items-center justify-between gap-2 px-4 py-2 text-sm text-neutral-600 dark:text-gray-300 hover:bg-neutral-200 dark:hover:bg-gray-700"
                >
                  <div class="flex items-center gap-2">
                    <Mail size={16} />
                    Inbox
                  </div>
                  {#if $inboxCount > 0}
                    <span
                      class="bg-danger-500 text-white text-xs font-semibold rounded-full px-2 py-0.5 min-w-[20px] text-center"
                    >
                      {$inboxCount > 9 ? '9+' : $inboxCount}
                    </span>
                  {/if}
                </Link>
                <Link
                  href={recommendations_path()}
                  class="flex items-center gap-2 px-4 py-2 text-sm text-neutral-600 dark:text-gray-300 hover:bg-neutral-200 dark:hover:bg-gray-700"
                >
                  <Send size={16} />
                  Recommendations
                </Link>
                <Link
                  href={discover_path()}
                  class="flex items-center gap-2 px-4 py-2 text-sm text-neutral-600 dark:text-gray-300 hover:bg-neutral-200 dark:hover:bg-gray-700"
                >
                  <Compass size={16} />
                  Discover
                </Link>
                <Link
                  href={challenges_path()}
                  class="flex items-center gap-2 px-4 py-2 text-sm text-neutral-600 dark:text-gray-300 hover:bg-neutral-200 dark:hover:bg-gray-700"
                >
                  <Award size={16} />
                  Challenges
                </Link>
                <Link
                  href={bestsellers_path()}
                  class="flex items-center gap-2 px-4 py-2 text-sm text-neutral-600 dark:text-gray-300 hover:bg-neutral-200 dark:hover:bg-gray-700"
                >
                  <TrendingUp size={16} />
                  Bestsellers
                </Link>
                <Link
                  href={random_path()}
                  class="flex items-center gap-2 px-4 py-2 text-sm text-neutral-600 dark:text-gray-300 hover:bg-neutral-200 dark:hover:bg-gray-700"
                >
                  <Shuffle size={16} />
                  Find Next Read
                </Link>
                <Link
                  href={ocr_scans_path()}
                  class="flex items-center gap-2 px-4 py-2 text-sm text-neutral-600 dark:text-gray-300 hover:bg-neutral-200 dark:hover:bg-gray-700"
                >
                  <Camera size={16} />
                  Book scans
                </Link>
                <Link
                  href={settings_path()}
                  class="flex items-center gap-2 px-4 py-2 text-sm text-neutral-600 dark:text-gray-300 hover:bg-neutral-200 dark:hover:bg-gray-700"
                >
                  <Settings size={16} />
                  Settings
                </Link>
              </div>
            {/snippet}
          </Dropdown>
        {:else}
          <Link
            href={sign_in_path()}
            class="px-3 sm:px-4 py-2 text-sm font-medium text-neutral-600 dark:text-gray-300 hover:text-neutral-900 dark:hover:text-white"
          >
            Sign in
          </Link>
          <Link href={sign_up_path()}>
            <Button>Sign up</Button>
          </Link>
        {/if}
      </div>
    </div>
  </div>
</nav>

<FlashMessage />
